import React, { useState, useEffect, useCallback, useMemo, useTransition } from 'react';
import { Table, Tag, Typography, Input, Button, Modal, Select, Pagination, Tooltip, Collapse, Checkbox, message, Result, Spin } from 'antd';
import { SearchOutlined, HistoryOutlined, EnvironmentOutlined, ArrowLeftOutlined, ReloadOutlined, UnorderedListOutlined, RobotOutlined, StarFilled, StarOutlined, FilterFilled } from '@ant-design/icons';
import { isAxiosError } from 'axios';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { ColumnsType, TableProps } from 'antd/es/table';
import { useUser } from '@/contexts/UserContext';
import { useTender } from '@/contexts/TenderContext';
import { formatUnit } from '@/utils/constants';
import {
  useRegionalLibraryItems,
  useLibraryHistory,
  useRefreshSiteLibrary,
  useSiteOppNo,
  useToggleFavourite,
} from '../hooks';
import { RATE_ENGINE_QUERY_KEYS } from '../api';
import type { CensoChange, RegionalLibraryItem, ResourceGroup } from '../types';
import { ChatPanel, ChangeHistoryModal, CensoSessionHistoryModal, parseUtc } from '../components/censo-panel';

const { Title, Text } = Typography;

// ─── Regions ──────────────────────────────────────────────────────────────────

const REGIONS = [
  { label: 'North West', value: 'north-west' },
  { label: 'West Midlands', value: 'west-midlands' },
  { label: 'East Midlands', value: 'east-midlands' },
  { label: 'Eastern Counties', value: 'eastern-counties' },
  { label: 'Southern', value: 'southern' },
  { label: 'South West', value: 'south-west' },
  { label: 'Yorkshire', value: 'yorkshire' },
  { label: 'Yorkshire (Morrisons)', value: 'YKSM' },
];

/** Convert a human-readable region label (e.g. "North West") to the API slug (e.g. "north-west"). Returns '' for "All Regions" or unknown values. */
function toRegionSlug(label: string | undefined): string {
  if (!label || label === 'All Regions') return '';
  const found = REGIONS.find(r => r.label.toLowerCase() === label.toLowerCase());
  return found?.value ?? label.toLowerCase().replace(/\s+/g, '-');
}

const RESOURCE_GROUPS: ResourceGroup[] = ['Labour', 'Plant', 'Material', 'Subcontract', 'Other'];

/** Sentinel `pageSize` value for the "Rows per page" Select's "All" option —
 *  shows every row on one page instead of a fixed page size. */
const ALL_ROWS_PAGE_SIZE = -1;

const RESOURCE_GROUP_COLOURS: Record<ResourceGroup, string> = {
  Labour: 'blue',
  Plant: 'orange',
  Material: 'green',
  Subcontract: 'purple',
  Other: 'default',
};

// ─── Memoized panel tables ──────────────────────────────────────────────────
// Extracted so each panel only re-renders when its OWN columns/dataSource/etc.
// actually change — otherwise every unrelated state change elsewhere in
// RegionalRate (opening a modal, the chat panel, sticky-offset measurement)
// would force antd to fully reconcile every row of a potentially large
// "Others" table (esp. now that "All" is the default page size).

interface PanelTableProps {
  columns: ColumnsType<RegionalLibraryItem>;
  dataSource: RegionalLibraryItem[];
  loading: boolean;
  pagination: TableProps<RegionalLibraryItem>['pagination'];
  scrollX: number;
  tableHeaderOffset: number;
  emptyText: string;
}

const PanelTable = React.memo(function PanelTable({
  columns,
  dataSource,
  loading,
  pagination,
  scrollX,
  tableHeaderOffset,
  emptyText,
}: PanelTableProps) {
  return (
    <Table<RegionalLibraryItem>
      rowKey="code"
      columns={columns}
      dataSource={dataSource}
      loading={loading}
      size="small"
      pagination={pagination}
      showSorterTooltip={false}
      scroll={{ x: scrollX }}
      sticky={{ offsetHeader: tableHeaderOffset, offsetScroll: 9999 }}
      locale={{ emptyText }}
    />
  );
});

// ─── Page ─────────────────────────────────────────────────────────────────────

const RegionalRate: React.FC = () => {
  const user = useUser();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { state: tenderState } = useTender();
  const [region, setRegion] = useState<string>('');
  const [regionApplied, setRegionApplied] = useState(false);
  const [adjustedRates, setAdjustedRates] = useState<Record<string, number>>({});
  const [adjustmentLabel, setAdjustmentLabel] = useState<string>('');
  const [appliedRates, setAppliedRates] = useState<Record<string, number>>({});
  const [search, setSearch] = useState<string>('');
  const [historyOpen, setHistoryOpen] = useState(false);
  const [sessionHistoryOpen, setSessionHistoryOpen] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState<string>('');
  const [discardedCodes, setDiscardedCodes] = useState<Set<string>>(new Set());
  const [pageSize, setPageSize] = useState<number>(ALL_ROWS_PAGE_SIZE);
  const [currentPage, setCurrentPage] = useState<number>(1);
  // Switching "Rows per page" (esp. to "All", which renders every filtered
  // row at once and can visibly lag) is wrapped in a transition so React can
  // yield to the browser mid-render — isPending flips (and paints the
  // Table's loading spinner) immediately, while the heavy re-render itself
  // proceeds without blocking the main thread in one synchronous commit.
  const [isPageSizeChangePending, startPageSizeChangeTransition] = useTransition();
  const [pageSizeDropdownOpen, setPageSizeDropdownOpen] = useState(false);
  // Independent Group filters per accordion panel — filtering Favourites
  // must not affect Other Items, and vice versa.
  const [favouriteGroupFilter, setFavouriteGroupFilter] = useState<ResourceGroup[]>([]);
  const [otherGroupFilter, setOtherGroupFilter] = useState<ResourceGroup[]>([]);
  // Draft selection inside each panel's Group filter dropdown — checkbox
  // ticks only edit this; the committed filter above (which actually
  // filters rows) only updates on OK, not per-checkbox.
  const [favouriteGroupDraft, setFavouriteGroupDraft] = useState<ResourceGroup[]>([]);
  const [otherGroupDraft, setOtherGroupDraft] = useState<ResourceGroup[]>([]);
  const [refreshModalOpen, setRefreshModalOpen] = useState(false);
  const [censoMinimized, setCensoMinimized] = useState(true);
  const [stickyOffset, setStickyOffset] = useState(230);
  const [panelHeaderOffset, setPanelHeaderOffset] = useState(270);
  const [tableHeaderOffset, setTableHeaderOffset] = useState(318);

  // Measure the sticky top section + accordion panel header once on mount so
  // the panel headers, and each panel's own table header below them, can
  // stick at the right offset — same approach as POS.tsx's stickyOffset.
  useEffect(() => {
    const el = document.querySelector('.rate-manager-sticky-header') as HTMLElement | null;
    const base = 88 + (el?.offsetHeight ?? 0);
    setStickyOffset(base);
    setPanelHeaderOffset(base);
    const collapseHeaderEl = document.querySelector('.rate-manager-collapse .ant-collapse-header') as HTMLElement | null;
    setTableHeaderOffset(base + (collapseHeaderEl?.offsetHeight ?? 48));
  }, []);

  useEffect(() => {
    // Double rAF ensures we run after paint + any focus-scroll triggered by
    // child components (e.g. Pagination auto-focus) on initial mount.
    let raf1: number;
    let raf2: number;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, []);

  useEffect(() => {
    if (regionApplied) return;
    // Project view: prefer the project's own region, fall back to user's region
    const fromProject = tenderState.projectName ? toRegionSlug(tenderState.region) : '';
    const fromUser = toRegionSlug(user?.region ?? '');
    const resolved = fromProject || fromUser;
    if (resolved) {
      setRegion(resolved);
      setRegionApplied(true);
    }
  }, [tenderState.region, tenderState.projectName, user?.region, regionApplied]);

  // Clear everything when region changes (ChatPanel session resets via key remount)
  useEffect(() => {
    setAdjustedRates({});
    setAdjustmentLabel('');
    setAppliedRates({});
    setCurrentSessionId('');
    setFavouriteGroupFilter([]);
    setOtherGroupFilter([]);
    setCurrentPage(1);
  }, [region]);

  const handleSessionIdChange = useCallback((id: string) => {
    setCurrentSessionId(id);
  }, []);

  const isRegional = !tenderState.projectName;
  // The Regional Rate Library (no tender in context) edits rates shared by
  // every future tender in the region, so it's restricted to System
  // Administrator — same role gate as the "Regional Rate Library" button on
  // the Tenders Dashboard (OpportunitiesDashboard.tsx's
  // canViewRegionalRateLibrary). Site-specific rates (a tender is open) stay
  // open to every role that already has access to this page.
  const isSystemAdministrator = user?.roleDisplayName === 'System Administrator';
  // Gates every auto-firing query below, not just the 403 view further down —
  // without this, `enabled: !!region` alone still fires every one of these
  // requests (they're declared before the early-return guard runs, since
  // hooks can't themselves be conditional) even for a non-admin who is about
  // to see nothing but the Access Restricted screen.
  const canAccessRateLibrary = !isRegional || isSystemAdministrator;
  const effectiveRegion = region || 'north-west';
  const siteName = tenderState.projectName || undefined;
  // Verified opportunity number for this site library — undefined for legacy
  // untagged sites, so they keep resolving by siteName alone.
  const { oppNo, isReady: isSiteScopeReady } = useSiteOppNo(effectiveRegion, siteName, tenderState.opportunityNo || undefined);
  const userEmail = user?.email ?? '';

  // Hide mutating controls (Reset, Rate Assistant) when viewing a historical
  // version. Deliberately does NOT check tenderState.isViewMode — the
  // Regional Rate Library is a standalone rate-management tool, not a
  // tender workflow page, so the tender's view/edit mode (MainLayout's
  // canEditTender) has no bearing on whether its own controls are usable.
  const isReadOnly = localStorage.getItem('isHistoricalVersion') === 'true';

  const refreshMutation = useRefreshSiteLibrary({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: RATE_ENGINE_QUERY_KEYS.libraryItems(effectiveRegion, siteName, oppNo) });
      queryClient.invalidateQueries({ queryKey: RATE_ENGINE_QUERY_KEYS.libraryHistory(effectiveRegion, siteName, undefined, oppNo) });
      setRefreshModalOpen(false);
      Modal.success({ title: 'Rates reset', content: 'Site library has been reset to regional defaults.' });
    },
    onError: (err) => {
      setRefreshModalOpen(false);
      if (isAxiosError(err) && err.response?.status === 404) {
        Modal.error({ title: 'Site not found', content: 'Site not found under this region.' });
      } else {
        Modal.error({ title: 'Reset failed', content: 'An error occurred. Please try again.' });
      }
    },
  });

  const { data, isLoading, isError } = useRegionalLibraryItems(effectiveRegion, siteName, { enabled: !!region && canAccessRateLibrary && isSiteScopeReady }, oppNo);
  const items = data ?? [];

  const toggleFavouriteMutation = useToggleFavourite({
    onSuccess: ({ code, isFavourite }) => {
      queryClient.setQueryData<RegionalLibraryItem[]>(
        RATE_ENGINE_QUERY_KEYS.libraryItems(effectiveRegion, siteName, oppNo),
        (old) => old?.map((item) => (item.code === code ? { ...item, isFavourite } : item)),
      );
    },
    onError: () => {
      message.error('Failed to update favourite. Please try again.');
    },
  });

  const handleToggleFavourite = useCallback((item: RegionalLibraryItem) => {
    toggleFavouriteMutation.mutate({
      region: effectiveRegion,
      code: item.code,
      isFavourite: !item.isFavourite,
      siteName,
      oppNo,
    });
  }, [toggleFavouriteMutation, effectiveRegion, siteName, oppNo]);

  const { data: historyData } = useLibraryHistory(effectiveRegion, siteName, undefined, { enabled: !!region && canAccessRateLibrary && isSiteScopeReady }, oppNo);

  const lastUpdated = useMemo(() => {
    if (!historyData) return null;
    // `entityType === 'ItemRate'` (the previous API shape's dedicated "rate
    // changed on an item" record kind) is now just any record whose
    // `entityKind` is `'item'` — `before`/`after`'s new object shape carries
    // whichever fields actually changed (including, but not limited to, rate).
    const itemRecords = historyData.filter((r) => r.entityKind === 'item');
    if (itemRecords.length === 0) return null;
    return itemRecords.reduce((latest, r) =>
      r.changedAt > latest.changedAt ? r : latest
    );
  }, [historyData]);

  // Scroll to top once data finishes loading — Pagination mounts at that point
  // and auto-focuses, which would otherwise push the page down.
  useEffect(() => {
    if (isLoading) return;
    let raf1: number;
    let raf2: number;
    raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        if (document.activeElement instanceof HTMLElement) {
          document.activeElement.blur();
        }
        window.scrollTo({ top: 0, left: 0, behavior: 'instant' });
      });
    });
    return () => {
      cancelAnimationFrame(raf1);
      cancelAnimationFrame(raf2);
    };
  }, [isLoading]);

  const filteredItems = useMemo(() => {
    let result = items;
    if (search.trim()) {
      const q = search.toLowerCase();
      result = result.filter(
        (item) =>
          item.code.toLowerCase().includes(q) ||
          item.name.toLowerCase().includes(q) ||
          item.resourceGroup.toLowerCase().includes(q) ||
          item.unit.toLowerCase().includes(q),
      );
    }
    return result;
  }, [items, search]);

  // Group filter is applied per-panel below — filtering one panel must not
  // affect the other's rows.
  const favouriteItems = useMemo(
    () =>
      filteredItems.filter(
        (item) =>
          item.isFavourite &&
          (favouriteGroupFilter.length === 0 ||
            favouriteGroupFilter.includes(item.resourceGroup as ResourceGroup)),
      ),
    [filteredItems, favouriteGroupFilter],
  );
  const otherItems = useMemo(
    () =>
      filteredItems.filter(
        (item) =>
          !item.isFavourite &&
          (otherGroupFilter.length === 0 ||
            otherGroupFilter.includes(item.resourceGroup as ResourceGroup)),
      ),
    [filteredItems, otherGroupFilter],
  );

  // "All" (ALL_ROWS_PAGE_SIZE) resolves to the current filtered row count so
  // the Table/Pagination always get a real positive pageSize, and stays "all
  // rows" even as that count changes with filtering.
  const effectivePageSize = pageSize === ALL_ROWS_PAGE_SIZE ? Math.max(otherItems.length, 1) : pageSize;

  const handlePageSizeChange = (val: number) => {
    // Close the dropdown as a normal (high-priority) update, separate from
    // the transition below, so it closes immediately instead of waiting on
    // the (potentially heavy) "All rows" re-render to finish.
    setPageSizeDropdownOpen(false);
    startPageSizeChangeTransition(() => {
      setPageSize(val);
      setCurrentPage(1);
    });
  };

  const hasAdjustments = Object.keys(adjustedRates).length > 0;

  const handleAdjust = (updated: Record<string, number>, summary: string) => {
    setAdjustedRates(updated);
    setAdjustmentLabel(summary);
  };

  const handleApply = (savedChanges: CensoChange[]) => {
    setAdjustedRates({});
    setAdjustmentLabel('');

    // Update display rates to reflect saved values
    const newApplied = { ...appliedRates };
    savedChanges.forEach((c) => { newApplied[c.c] = c.nr; });
    setAppliedRates(newApplied);
  };

  // Build table columns — New Rate column only when AI adjustments are
  // pending. Each panel calls this with its own group-filter state/setter
  // (and an optional onApply, e.g. to reset pagination) so filtering one
  // panel's Group column never touches the other panel's rows.
  const buildColumns = useCallback((
    groupFilter: ResourceGroup[],
    setGroupFilter: (groups: ResourceGroup[]) => void,
    groupDraft: ResourceGroup[],
    setGroupDraft: (groups: ResourceGroup[]) => void,
    onApply?: () => void,
  ): ColumnsType<RegionalLibraryItem> => [
    {
      title: '',
      dataIndex: 'isFavourite',
      key: 'isFavourite',
      width: 40,
      align: 'center',
      render: (isFavourite: boolean, record: RegionalLibraryItem) => (
        <Tooltip title={isFavourite ? 'Remove from favourites' : 'Mark as favourite'}>
          <Button
            type="text"
            size="small"
            icon={isFavourite
              ? <StarFilled style={{ color: '#fadb14' }} />
              : <StarOutlined style={{ color: '#bbb' }} />}
            onClick={() => handleToggleFavourite(record)}
          />
        </Tooltip>
      ),
    },
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      width: 120,
      sorter: (a, b) => a.code.localeCompare(b.code),
    },
    {
      title: 'Cost Code',
      dataIndex: 'costCode',
      key: 'costCode',
      width: 110,
      sorter: (a, b) => (a.costCode ?? '').localeCompare(b.costCode ?? ''),
      render: (value: string | null | undefined) => value ?? '—',
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      width: 260,
      ellipsis: { showTitle: false },
      sorter: (a, b) => a.name.localeCompare(b.name),
      render: (name: string) => (
        <Tooltip title={name}>
          <span>{name}</span>
        </Tooltip>
      ),
    },
    {
      title: 'Group',
      dataIndex: 'resourceGroup',
      key: 'resourceGroup',
      width: 130,
      // Custom filter icon/dropdown driven directly by this panel's own
      // group-filter state (not antd's own filteredValue/onChange), so
      // filtering this panel never affects the other one.
      filterIcon: () => (
        <FilterFilled style={{ color: groupFilter.length > 0 ? '#1890ff' : undefined }} />
      ),
      filterDropdown: ({ confirm }) => (
        <div style={{ padding: 12, width: 180 }}>
          <Checkbox.Group
            value={groupDraft}
            onChange={(vals) => setGroupDraft(vals as ResourceGroup[])}
            style={{ display: 'flex', flexDirection: 'column', gap: 8 }}
          >
            {RESOURCE_GROUPS.map((g) => (
              <Checkbox key={g} value={g}>{g}</Checkbox>
            ))}
          </Checkbox.Group>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12 }}>
            <Button
              size="small"
              type="link"
              onClick={() => { setGroupDraft([]); setGroupFilter([]); onApply?.(); confirm(); }}
            >
              Reset
            </Button>
            <Button
              size="small"
              type="primary"
              onClick={() => { setGroupFilter(groupDraft); onApply?.(); confirm(); }}
            >
              OK
            </Button>
          </div>
        </div>
      ),
      onFilterDropdownOpenChange: (open) => { if (open) setGroupDraft(groupFilter); },
      render: (rg: ResourceGroup) => <Tag color={RESOURCE_GROUP_COLOURS[rg]}>{rg}</Tag>,
    },
    {
      title: 'Rate (£)',
      dataIndex: 'rate',
      key: 'rate',
      width: 100,
      align: 'right',
      sorter: (a, b) => a.rate - b.rate,
      render: (rate: number, record: RegionalLibraryItem) => {
        const effective = appliedRates[record.code] ?? rate;
        return (
          <span style={{ fontWeight: appliedRates[record.code] !== undefined ? 600 : undefined }}>
            {effective.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </span>
        );
      },
    },
    ...(hasAdjustments
      ? [
          {
            title: (
              <span>
                New Rate (£){' '}
                <Tag color="green" style={{ marginLeft: 4, fontWeight: 400, fontSize: '11px' }}>
                  {adjustmentLabel}
                </Tag>
              </span>
            ),
            key: 'newRate',
            width: 160,
            align: 'right' as const,
            render: (_: unknown, record: RegionalLibraryItem) => {
              const newRate = adjustedRates[record.code];
              if (newRate === undefined) {
                return (
                  <span style={{ color: '#aaa' }}>
                    {record.rate.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                );
              }
              const base = appliedRates[record.code] ?? record.rate;
              const diff = newRate - base;
              return (
                <span style={{ color: diff > 0 ? '#52c41a' : diff < 0 ? '#ff4d4f' : 'inherit', fontWeight: 600 }}>
                  {newRate.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
              );
            },
          },
        ]
      : []),
    {
      title: 'Unit',
      dataIndex: 'unit',
      key: 'unit',
      width: 65,
      render: (value: string) => formatUnit(value),
    },
  ], [hasAdjustments, adjustmentLabel, adjustedRates, appliedRates, handleToggleFavourite]);

  // Resets "Others" pagination to page 1 whenever its own Group filter is
  // applied — stable identity so it doesn't itself force otherColumns to
  // rebuild every render.
  const resetOtherPage = useCallback(() => setCurrentPage(1), []);

  const favouriteColumns = useMemo(
    () => buildColumns(favouriteGroupFilter, setFavouriteGroupFilter, favouriteGroupDraft, setFavouriteGroupDraft),
    [buildColumns, favouriteGroupFilter, favouriteGroupDraft],
  );
  const otherColumns = useMemo(
    () => buildColumns(otherGroupFilter, setOtherGroupFilter, otherGroupDraft, setOtherGroupDraft, resetOtherPage),
    [buildColumns, otherGroupFilter, otherGroupDraft, resetOtherPage],
  );
  // sticky forces the header into a separate DOM table whose table-layout: auto
  // ignores the (hidden) body <thead> width, so body columns collapse/wrap on
  // narrow screens unless every column has an explicit width and scroll.x is
  // numeric (forces table-layout: fixed) rather than 'max-content'.
  const tableScrollX = 40 + 120 + 110 + 260 + 130 + 100 + 65 + (hasAdjustments ? 160 : 0);

  const otherPagination = useMemo<TableProps<RegionalLibraryItem>['pagination']>(() => ({
    current: currentPage,
    pageSize: effectivePageSize,
    total: otherItems.length,
    showSizeChanger: false,
    onChange: (page: number) => setCurrentPage(page),
    className: 'rate-manager-others-pagination-hidden',
  }), [currentPage, effectivePageSize, otherItems.length]);

  if (user?.loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 'calc(100vh - 112px)' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (!canAccessRateLibrary) {
    return (
      <div style={{ padding: '24px', minHeight: 'calc(100vh - 112px)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Result
          status="403"
          title="Access Restricted"
          subTitle="Only System Administrators can access the Regional Rate Library."
          extra={
            <Button type="primary" icon={<ArrowLeftOutlined />} onClick={() => navigate(-1)}>
              Go Back
            </Button>
          }
        />
      </div>
    );
  }

  return (
    <div style={{ padding: '24px', minHeight: 'calc(100vh - 112px)', display: 'flex', flexDirection: 'column' }}>
      {/* Sticky top section — header row + scope info strip stay pinned below the
          fixed 88px app header (MainLayout.tsx) while the rest of the page scrolls.
          Negative margin + matching padding on all sides reclaim the parent's own
          padding so the sticky background covers that gap too, instead of showing
          a flash of unstuck content around it. backgroundColor matches the
          Regional/Site pill badge's own orange/green theme below it. */}
      <div className="rate-manager-sticky-header" style={{ position: 'sticky', top: '88px', zIndex: 98, backgroundColor: isRegional ? '#eff6ff' : '#f0fdf4', margin: '-24px -24px 0', padding: '24px 24px 0' }}>
      {/* Header */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          marginBottom: '16px',
          flexShrink: 0,
          flexWrap: 'wrap',
        }}
      >
        <Title level={4} style={{ margin: 0 }}>
          {!isRegional ? 'Site Rate Library' : 'Regional Rate Library'}
        </Title>
        <Tooltip
          title={isRegional
            ? 'You are editing the Regional Rate Library. Changes will affect all new tenders in this region.'
            : 'You are editing site-specific rates. Changes apply only to this tender.'}
        >
          <span style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 5,
            padding: '3px 10px',
            borderRadius: 20,
            backgroundColor: isRegional ? '#fff7ed' : '#f0fdf4',
            border: `1px solid ${isRegional ? '#fdba74' : '#86efac'}`,
            color: isRegional ? '#c2410c' : '#15803d',
            fontSize: 12,
            fontWeight: 600,
            letterSpacing: '0.02em',
            cursor: 'default',
            userSelect: 'none',
          }}>
            <span style={{
              width: 7,
              height: 7,
              borderRadius: '50%',
              backgroundColor: isRegional ? '#f97316' : '#22c55e',
              flexShrink: 0,
            }} />
            {isRegional ? 'Regional Library' : 'Site Rates'}
          </span>
        </Tooltip>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '6px',
            padding: '4px 12px',
            borderRadius: '20px',
            backgroundColor: '#e6f0ff',
            border: '1px solid #91b8ff',
            color: '#0B2545',
            fontSize: '13px',
            fontWeight: 600,
            lineHeight: 1.5,
          }}
        >
          <EnvironmentOutlined style={{ fontSize: '13px', color: '#1890ff' }} />
          {!isRegional
            ? `${tenderState.projectName} | ${REGIONS.find((r) => r.value === effectiveRegion)?.label ?? effectiveRegion}`
            : REGIONS.find((r) => r.value === effectiveRegion)?.label ?? effectiveRegion}
        </span>
        <Input
          placeholder="Search code, name, group…"
          prefix={<SearchOutlined style={{ color: '#bbb' }} />}
          value={search}
          onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
          allowClear
          style={{ width: 220 }}
        />

        <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
          {!isReadOnly && (
            <Tooltip title="View Session History">
              <Button icon={<RobotOutlined />} onClick={() => setSessionHistoryOpen(true)} />
            </Tooltip>
          )}
          <Tooltip title="Switch to Activity View">
            <Button icon={<UnorderedListOutlined />} onClick={() => navigate('/rate-engine/activity-tree')}>
              Activity View
            </Button>
          </Tooltip>
          <Button icon={<HistoryOutlined />} onClick={() => setHistoryOpen(true)}>
            Change History
          </Button>
          {!isRegional && !isReadOnly && (
            <Button
              icon={<ReloadOutlined />}
              danger
              onClick={() => setRefreshModalOpen(true)}
            >
              Reset to Regional Defaults
            </Button>
          )}
          {isRegional && (
            <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/')} size="middle">
              Back to Dashboard
            </Button>
          )}
        </div>

        <Modal
          open={refreshModalOpen}
          title="Reset to Regional Defaults"
          okText="Reset"
          okButtonProps={{ danger: true, loading: refreshMutation.isPending }}
          cancelButtonProps={{ disabled: refreshMutation.isPending }}
          onOk={() => {
            if (!siteName) return;
            refreshMutation.mutate({ region: effectiveRegion, siteName, refreshedBy: userEmail, oppNo });
          }}
          onCancel={() => { if (!refreshMutation.isPending) setRefreshModalOpen(false); }}
          closable={!refreshMutation.isPending}
        >
          <p>
            Resetting will discard all rate changes made to this site and restore it to the
            current regional library. It will also remove all favourites from the Site Rate
            library and replace them with the favourites from the regional library.
          </p>
          <p>This action cannot be undone. Continue?</p>
        </Modal>
      </div>

      {/* Scope info strip */}
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        padding: '10px 14px',
        marginBottom: 12,
        borderRadius: 6,
        flexShrink: 0,
        backgroundColor: isRegional ? '#dbeafe' : '#d1fae5',
        borderLeft: `4px solid ${isRegional ? '#3b82f6' : '#10b981'}`,
      }}>
        <div>
          <Text strong style={{ fontSize: 13, color: isRegional ? '#1e40af' : '#065f46' }}>
            {isRegional ? 'Regional Rate Library' : 'Site-Specific Rates'}
          </Text>
          <Text style={{ display: 'block', fontSize: 12, color: isRegional ? '#3730a3' : '#047857', marginTop: 1 }}>
            {isRegional
              ? 'Changes here apply to all new tenders in this region — please review carefully before saving.'
              : 'Changes here apply only to this tender and will not affect the regional library or other tenders.'}
          </Text>
        </div>
      </div>
      </div>

      {/* Body */}
      <div style={{ display: 'flex', gap: '16px', flex: 1 }}>
        {/* Table */}
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', backgroundColor: '#fff', borderRadius: 8, border: '1px solid rgba(0,0,0,0.08)', padding: '0 0 0 0' }}>
          {lastUpdated && (
            <div style={{ padding: '8px 12px', borderBottom: '1px solid #f0f0f0', flexShrink: 0 }}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                <HistoryOutlined style={{ marginRight: 5 }} />
                Last updated:{' '}
                <strong>
                  {parseUtc(lastUpdated.changedAt).toLocaleString('en-GB', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </strong>
                {' '}by <strong>{lastUpdated.changedBy}</strong>
              </Text>
            </div>
          )}
          <Collapse
            className="rate-manager-collapse"
            defaultActiveKey={['favourites', 'others']}
            bordered={false}
            style={{ backgroundColor: '#fff' }}
            items={[
              {
                key: 'favourites',
                label: <Text strong>⭐ Favourites ({favouriteItems.length})</Text>,
                children: (
                  <PanelTable
                    columns={favouriteColumns}
                    dataSource={favouriteItems}
                    loading={isLoading}
                    pagination={false}
                    scrollX={tableScrollX}
                    tableHeaderOffset={tableHeaderOffset}
                    emptyText="No favourites yet — click the star icon on any item to add it here."
                  />
                ),
              },
              {
                key: 'others',
                label: <Text strong>Others ({otherItems.length})</Text>,
                // Rendered into the panel header's own `.ant-collapse-extra` slot
                // (right-aligned there by antd's default header layout) — this
                // also makes it sticky for free, since it's inside the same
                // `.ant-collapse-header` the CSS below already pins with
                // `position: sticky`. `stopPropagation` keeps interacting with
                // the Select/Pagination from also toggling the accordion, since
                // antd doesn't guard extra-content clicks against that itself.
                extra: (
                  <div
                    onClick={(e) => e.stopPropagation()}
                    style={{ display: 'flex', alignItems: 'center', gap: 12 }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 13, color: '#888' }}>Rows per page</span>
                      <Select
                        value={pageSize}
                        onChange={handlePageSizeChange}
                        open={pageSizeDropdownOpen}
                        onOpenChange={setPageSizeDropdownOpen}
                        options={[
                          { value: 10, label: '10' },
                          { value: 20, label: '20' },
                          { value: 50, label: '50' },
                          { value: 100, label: '100' },
                          { value: ALL_ROWS_PAGE_SIZE, label: 'All' },
                        ]}
                        style={{ width: 70 }}
                        size="small"
                      />
                    </div>
                    <Pagination
                      current={currentPage}
                      pageSize={effectivePageSize}
                      total={otherItems.length}
                      onChange={(page) => setCurrentPage(page)}
                      showTotal={(total) => `${total} items`}
                      size="small"
                      showSizeChanger={false}
                      hideOnSinglePage={pageSize === ALL_ROWS_PAGE_SIZE}
                    />
                  </div>
                ),
                children: (
                  // Pagination is driven by antd's own `pagination` config (rather than
                  // manually slicing dataSource) so its built-in column sorters run against
                  // the FULL filtered dataset before paginating — a manual pre-slice made
                  // sorting (and would make any future filter) only apply within the
                  // current page. The default pager UI is hidden via CSS; the visible
                  // Select + Pagination controls in the panel header (above) drive/read
                  // the same state.
                  <PanelTable
                    columns={otherColumns}
                    dataSource={otherItems}
                    loading={isLoading || isPageSizeChangePending}
                    pagination={otherPagination}
                    scrollX={tableScrollX}
                    tableHeaderOffset={tableHeaderOffset}
                    emptyText={isError ? 'Failed to load items' : 'No items found'}
                  />
                ),
              },
            ]}
          />
        </div>

        {/* Chat panel — inline, hidden when minimised (remounts on region/site change to reset
            Censo session). Not mounted until the region has settled (regionApplied) so it
            never auto-connects against the transient 'north-west' fallback and then immediately
            remounts/reconnects. */}
        {regionApplied && (
        <div style={{
          display: isReadOnly || censoMinimized ? 'none' : 'flex',
          width: 360,
          flexShrink: 0,
          flexDirection: 'column',
          borderRadius: 8,
          border: '1px solid rgba(0,0,0,0.08)',
          overflow: 'hidden',
          position: 'sticky',
          top: `${stickyOffset}px`,
          alignSelf: 'flex-start',
          height: `calc(100vh - ${stickyOffset}px - 16px)`,
        }}>
          <ChatPanel
            key={`${effectiveRegion}::${siteName ?? ''}`}
            items={items}
            region={effectiveRegion}
            siteName={siteName}
            oppNo={oppNo}
            tenderName={tenderState.projectName || undefined}
            userEmail={userEmail}
            adjustedRates={adjustedRates}
            onAdjust={handleAdjust}
            onApply={handleApply}
            onSessionIdChange={handleSessionIdChange}
            onDiscardedCodesChange={setDiscardedCodes}
            isOpen={!isReadOnly && !censoMinimized}
            onMinimize={() => setCensoMinimized(true)}
          />
        </div>
        )}
      </div>

      {/* Floating bubble — shown when panel is minimised and not in read-only mode */}
      {regionApplied && censoMinimized && !isReadOnly && (
        <button
          onClick={() => {
            setCensoMinimized(false);
            window.dispatchEvent(
              new CustomEvent('sidebarCollapseRequest', { detail: { collapsed: true } }),
            );
          }}
          title="Open Rate Assistant"
          style={{
            position: 'fixed',
            bottom: 28,
            right: 28,
            width: 58,
            height: 58,
            borderRadius: '50%',
            backgroundColor: '#0B2545',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 4px 16px rgba(11,37,69,0.45)',
            zIndex: 1200,
            transition: 'transform 0.15s ease, box-shadow 0.15s ease',
          }}
          onMouseEnter={e => {
            (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1.08)';
            (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 6px 22px rgba(11,37,69,0.55)';
          }}
          onMouseLeave={e => {
            (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)';
            (e.currentTarget as HTMLButtonElement).style.boxShadow = '0 4px 16px rgba(11,37,69,0.45)';
          }}
        >
          <RobotOutlined style={{ color: '#fff', fontSize: 24 }} />
          {hasAdjustments && (
            <span style={{
              position: 'absolute', top: 4, right: 4,
              width: 17, height: 17, borderRadius: '50%',
              backgroundColor: '#fa8c16', color: '#fff',
              fontSize: 9, fontWeight: 700,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {Object.keys(adjustedRates).length}
            </span>
          )}
        </button>
      )}

      {/* Change History Modal — fetches from API, grouped by batchId */}
      <ChangeHistoryModal
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        region={effectiveRegion}
        siteName={siteName}
        oppNo={oppNo}
        items={items}
      />

      {/* AI Session History Modal — Censo exchange log for current session */}
      <CensoSessionHistoryModal
        open={sessionHistoryOpen}
        onClose={() => setSessionHistoryOpen(false)}
        sessionId={currentSessionId}
        discardedCodes={discardedCodes}
      />

      <style>{`
        /* Sticky accordion panel headers — must sit above table cell stacking
           contexts, but below the sticky top section (z-index: 98). */
        .rate-manager-collapse .ant-collapse-header {
          position: sticky !important;
          top: ${panelHeaderOffset}px !important;
          z-index: 90 !important;
          background-color: #fff !important;
        }
        /* antd's own pager for the "Others" table is hidden — the visible
           Select + Pagination bar in the panel header (its "extra" slot)
           drives the same current/pageSize state, while antd's config still
           does the real (post-sort, whole-dataset) pagination internally. */
        .rate-manager-others-pagination-hidden {
          display: none !important;
        }
      `}</style>
    </div>
  );
};

export default RegionalRate;
